import { useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { SaveOrgWideCompetencyMutationVariables } from "types/graphql-schema";

import { TeamPickerTeam } from "@apps/reporting/components/team-picker";
import Modal from "@components/modal/modal";
import useConfirm from "@helpers/hooks/use-confirm";

import { CareerTrackPickerTrack } from "./career-track-picker";
import CompetencyForm from "./competency-form";

const CompetencyFormModal = ({
  organizationId,
  competencyId,
  startingData,
  startingTeams,
  startingCareerTracks,
  onClose,
  onCompetencySaved,
  showSharedWarning,
  onCompetencyDeleted,
}: {
  organizationId: number;
  competencyId?: number;
  startingData?: Partial<
    Omit<SaveOrgWideCompetencyMutationVariables, "criteria">
  >;
  startingTeams?: TeamPickerTeam[];
  startingCareerTracks?: CareerTrackPickerTrack[];
  onClose: () => void;
  onCompetencySaved: (competencyId: number) => void;
  showSharedWarning?: boolean;
  onCompetencyDeleted?: () => void;
}) => {
  // A new competency starts with an empty name, so start there.
  const titleInputRef = useRef<HTMLInputElement>(null);
  // Escape, a click outside and the close button all ask first when there
  // are unsaved changes.
  const isDirtyRef = useRef(false);
  const handleDirtyChange = useCallback((isDirty: boolean) => {
    isDirtyRef.current = isDirty;
  }, []);
  const { confirm: confirmDiscard, ConfirmationDialog: ConfirmDiscardDialog } =
    useConfirm("Discard changes?", "The changes you made will be lost.");
  const handleDismiss = useCallback(async () => {
    if (isDirtyRef.current && !(await confirmDiscard())) {
      return;
    }
    onClose();
  }, [confirmDiscard, onClose]);
  return createPortal(
    <Modal
      open
      onClose={handleDismiss}
      alignedTop
      dialogClassName="md:max-w-4xl"
      aria-label="Competency form modal"
      initialFocus={competencyId ? undefined : titleInputRef}
    >
      <div className="flex flex-col">
        <ConfirmDiscardDialog />
        <CompetencyForm
          organizationId={organizationId}
          competencyId={competencyId}
          titleInputRef={titleInputRef}
          startingData={startingData}
          startingTeams={startingTeams}
          startingCareerTracks={startingCareerTracks}
          onCancel={onClose}
          onDismiss={handleDismiss}
          onDirtyChange={handleDirtyChange}
          onCompetencySaved={(id) => {
            onCompetencySaved(id);
            onClose();
          }}
          showSharedWarning={showSharedWarning}
          onCompetencyDeleted={onCompetencyDeleted}
        />
      </div>
    </Modal>,
    document.body,
  );
};

export default CompetencyFormModal;
